import type { Logger } from "../utils/logger.js";

/**
 * Debounces an async persist. Errors are observable two ways: logged at warn
 * immediately, AND retained so the next flush()/cancel() re-throws (recovery
 * contract — the caller learns of a failed persist at the next sync point).
 */
export class Debouncer {
  #timer: NodeJS.Timeout | null = null;
  #pending = false;
  #disabled = false;
  #lastError: unknown = null;
  readonly #logger: Logger | undefined;

  constructor(
    private readonly fn: () => Promise<void>,
    private readonly ms: number,
    opts: { logger?: Logger } = {},
  ) {
    this.#logger = opts.logger;
  }

  schedule(): void {
    if (this.#disabled) return;
    this.#pending = true;
    if (this.#timer !== null) return;
    this.#timer = setTimeout(() => {
      this.#timer = null;
      if (!this.#pending) return;
      this.#pending = false;
      // Capture rejections so the next flush()/cancel() can surface them
      // to the caller. Without this, a failed disk write (e.g. ENOSPC)
      // would silently disappear and the download would keep running as
      // if state were durable.
      void this.fn().catch((err: unknown) => {
        this.#lastError = err;
        this.#logger?.warn({ err }, "debounced persist failed; will re-throw at next flush");
      });
    }, this.ms);
  }

  async flush(): Promise<void> {
    if (this.#timer !== null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
    if (this.#pending) {
      this.#pending = false;
      try {
        await this.fn();
      } catch (err: unknown) {
        this.#lastError = err;
      }
    }
    if (this.#lastError !== null) {
      const err = this.#lastError;
      this.#lastError = null;
      throw err;
    }
  }

  // Drain any pending work and refuse future schedule() calls.
  // Used on cancellation so in-flight tasks completing after the
  // download function returns can't reschedule a stale state-file write.
  async cancel(): Promise<void> {
    this.#disabled = true;
    await this.flush();
  }
}
